import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import React, { useState, useCallback, useEffect } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import useAxios from '../../../../../hooks/useAxios'
import {
  SearchOutlined,
  InfoOutlined,
  Search,
  Close,
  NavigateNext,
  NavigateBefore,
} from '@mui/icons-material'
import {
  Table,
  TableColumnsType,
  Spin,
  Empty,
  message,
  Tooltip,
  Pagination,
} from 'antd'
import { FiCopy } from 'react-icons/fi'
import {
  ITikTokAccount,
  ITiktokProductsResult,
} from '../../../../../types/tiktok'

const PRODUCTS_PAGE_SIZE = 20

interface IProduct {
  id: string
  name: string
}

interface IProductPerformance {
  latest_available_date: string
  performance: {
    intervals: Array<{
      avg_page_visitor_breakdowns: Array<{ amount: number; type: string }>
      avg_page_visitors: number
      click_through_rate: string
      click_through_rate_breakdowns: Array<{ amount: string; type: string }>
      end_date: string
      gmv: { amount: string; currency: string }
      gmv_breakdowns: Array<{ amount: string; currency: string; type: string }>
      impression_breakdowns: Array<{ amount: number; type: string }>
      impressions: number
      orders: number
      page_view_breakdowns: Array<{ amount: number; type: string }>
      page_views: number
      start_date: string
      unit_sold_breakdowns: Array<{ amount: number; type: string }>
      units_sold: number
    }>
  }
  shop: {
    name: string
    region: string
    seller_type: string
  }
}

interface ProductPerformanceData {
  key: string
  numberRow: number
  start_date: string
  end_date: string
  gmv: { amount: string; currency: string }
  gmv_breakdowns: Array<{ amount: string; currency: string; type: string }>
  impressions: number
  impression_breakdowns: Array<{ amount: number; type: string }>
  page_views: number
  page_view_breakdowns: Array<{ amount: number; type: string }>
  avg_page_visitors: number
  avg_page_visitor_breakdowns: Array<{ amount: number; type: string }>
  click_through_rate: string
  click_through_rate_breakdowns: Array<{ amount: string; type: string }>
  orders: number
  units_sold: number
  unit_sold_breakdowns: Array<{ amount: number; type: string }>
}

interface ProductReportCardProps {
  tiktokAccounts: ITikTokAccount[]
}

const ProductReportCard: React.FC<ProductReportCardProps> = ({
  tiktokAccounts,
}) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(7, 'day'),
  )
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs())
  const [selectedProduct, setSelectedProduct] = useState<string>('')
  const [selectedProductName, setSelectedProductName] = useState<string>('')
  const [visitedReport, setVisitedReport] = useState<boolean>(true)
  const [isProductModalOpen, setIsProductModalOpen] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState<number>(1)
  // TikTok แบ่งหน้าด้วย cursor — เก็บ token ของทุกหน้าที่เดินผ่านมาเพื่อย้อนกลับได้
  // ช่องแรกว่างเสมอเพราะหน้าแรกไม่ต้องส่ง token
  const [pageTokens, setPageTokens] = useState<string[]>([''])
  const [searchParams, setSearchParams] = useState({
    startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    shopId: tiktokAccounts?.[0]?.shopId,
  })

  const [shopId, setShopId] = useState<string>(tiktokAccounts?.[0]?.shopId)

  // Fetch products list with pagination
  const products = useAxios<ITiktokProductsResult<IProduct>>({
    method: 'GET',
    url: '/tiktok/products',
    params: {
      pageToken: pageTokens[currentPage - 1] ?? '',
      pageSize: PRODUCTS_PAGE_SIZE,
      shopId: shopId,
    },
  })

  const productList = products.data?.products ?? []
  const nextPageToken = products.data?.nextPageToken ?? ''

  // Fetch product performance data
  const productPerformance = useAxios<IProductPerformance>(
    visitedReport && selectedProduct
      ? {
          method: 'GET',
          url: `/tiktok/report/shop/product/${selectedProduct}`,
          params: {
            startDate: searchParams.startDate,
            endDate: searchParams.endDate,
            granularity: '1D',
            shopId: searchParams.shopId,
          },
        }
      : null,
  )

  // Handle date changes
  const handleStartDateChange = (date: Dayjs | null) => {
    setStartDate(date)
  }

  const handleEndDateChange = (date: Dayjs | null) => {
    setEndDate(date)
  }

  // Handle product selection
  const handleProductSelect = (product: IProduct) => {
    setSelectedProduct(product.id)
    setSelectedProductName(product.name)
    setIsProductModalOpen(false)
  }

  // Handle modal open/close
  const handleOpenProductModal = () => {
    setIsProductModalOpen(true)
  }

  const handleCloseProductModal = () => {
    setIsProductModalOpen(false)
    setCurrentPage(1)
    setPageTokens([''])
  }

  // Handle pagination
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (!nextPageToken) {
      return
    }
    setPageTokens((tokens) => {
      const next = [...tokens]
      next[currentPage] = nextPageToken
      return next
    })
    setCurrentPage(currentPage + 1)
  }

  // Handle search button click
  const handleSearch = useCallback(() => {
    const formattedStartDate = startDate ? startDate.format('YYYY-MM-DD') : ''
    const formattedEndDate = endDate ? endDate.format('YYYY-MM-DD') : ''

    setSearchParams({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      shopId: shopId,
    })
    setVisitedReport(true)
  }, [startDate, endDate])

  // Format currency values
  const formatCurrency = (value: string) => {
    return parseFloat(value).toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  const handleCopyText = (text: string) => {
    navigator.clipboard.writeText(text)
    message.success(`คัดลอกแล้ว: ${text}`)
  }

  // Define columns for Antd Table
  const columns: TableColumnsType<ProductPerformanceData> = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center',
      width: 80,
      fixed: 'left',
      render: (numberRow: number) => (
        <Typography
          variant="subtitle1"
          color="text.secondary"
          textAlign="center"
        >
          {numberRow}
        </Typography>
      ),
    },
    {
      title: 'วันที่',
      dataIndex: 'start_date',
      key: 'start_date',
      align: 'center',
      width: 120,
      fixed: 'left',
      render: (date: string) => (
        <Typography variant="subtitle1">
          {dayjs(date).format('DD/MM/YYYY')}
        </Typography>
      ),
    },
    {
      title: 'ยอดขาย (GMV)',
      dataIndex: 'gmv',
      key: 'gmv',
      align: 'right',
      width: 150,
      render: (gmv: { amount: string; currency: string }) => (
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={0.5}
        >
          <Typography variant="subtitle1" color="success.main">
            {formatCurrency(gmv.amount)} {gmv.currency}
          </Typography>
          <FiCopy
            size={14}
            style={{ cursor: 'pointer' }}
            onClick={() =>
              handleCopyText(`${formatCurrency(gmv.amount)} ${gmv.currency}`)
            }
          />
        </Box>
      ),
    },
    {
      title: (
        <Tooltip title="GMV Breakdowns แสดงรายละเอียดของยอดขายแยกตามประเภท">
          <span>
            GMV Breakdowns <InfoOutlined style={{ fontSize: 14 }} />
          </span>
        </Tooltip>
      ),
      dataIndex: 'gmv_breakdowns',
      key: 'gmv_breakdowns',
      align: 'right',
      width: 180,
      render: (
        breakdowns: Array<{ amount: string; currency: string; type: string }>,
      ) => (
        <Box>
          {breakdowns?.map((item, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {item.type}: {formatCurrency(item.amount)} {item.currency}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      title: 'จำนวนการเข้าชม',
      dataIndex: 'impressions',
      key: 'impressions',
      align: 'center',
      width: 120,
      render: (impressions: number) => (
        <Typography variant="subtitle1">{impressions}</Typography>
      ),
    },
    {
      title: 'จำนวนการดูหน้า',
      dataIndex: 'page_views',
      key: 'page_views',
      align: 'center',
      width: 120,
      render: (pageViews: number) => (
        <Typography variant="subtitle1">{pageViews}</Typography>
      ),
    },
    {
      title: 'จำนวนผู้เข้าชมเฉลี่ย',
      dataIndex: 'avg_page_visitors',
      key: 'avg_page_visitors',
      align: 'center',
      width: 150,
      render: (visitors: number) => (
        <Typography variant="subtitle1">{visitors}</Typography>
      ),
    },
    {
      title: 'อัตราการคลิก',
      dataIndex: 'click_through_rate',
      key: 'click_through_rate',
      align: 'center',
      width: 120,
      render: (rate: string) => (
        <Typography variant="subtitle1">
          {(parseFloat(rate) * 100).toFixed(2)}%
        </Typography>
      ),
    },
    {
      title: 'จำนวนคำสั่งซื้อ',
      dataIndex: 'orders',
      key: 'orders',
      align: 'center',
      width: 120,
      render: (orders: number) => (
        <Typography variant="subtitle1">{orders}</Typography>
      ),
    },
    {
      title: 'จำนวนสินค้าที่ขายได้',
      dataIndex: 'units_sold',
      key: 'units_sold',
      align: 'center',
      width: 150,
      render: (units: number) => (
        <Typography variant="subtitle1">{units}</Typography>
      ),
    },
  ]

  // Prepare data for Antd Table - sort by date (newest first)
  const tableData: ProductPerformanceData[] = [
    ...(productPerformance.data?.performance?.intervals || []),
  ]
    .sort(
      (a, b) =>
        new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
    )
    .map((interval, index) => ({
      ...interval,
      key: interval.start_date,
      numberRow: index + 1,
    }))

  // Product selection modal
  const ProductSelectionModal = () => (
    <Dialog
      open={isProductModalOpen}
      onClose={handleCloseProductModal}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">เลือกสินค้า</Typography>
          <IconButton onClick={handleCloseProductModal}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Spin spinning={products.isLoading}>
          {products.error ? (
            <Typography color="error">
              เกิดข้อผิดพลาดในการโหลดข้อมูล: {products.error.message}
            </Typography>
          ) : productList.length ? (
            <Box>
              {productList.map((product) => (
                <Box
                  key={product.id}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  onClick={() => handleProductSelect(product)}
                >
                  <Typography variant="subtitle1">{product.name}</Typography>
                </Box>
              ))}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={2}
              >
                <Button
                  startIcon={<NavigateBefore />}
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  ก่อนหน้า
                </Button>
                <Typography variant="body2" color="text.secondary">
                  หน้า {currentPage}
                </Typography>
                <Button
                  endIcon={<NavigateNext />}
                  onClick={handleNextPage}
                  disabled={!nextPageToken}
                >
                  ถัดไป
                </Button>
              </Box>
            </Box>
          ) : (
            <Empty description="ไม่พบสินค้า" />
          )}
        </Spin>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseProductModal}>ปิด</Button>
      </DialogActions>
    </Dialog>
  )

  return (
    <Card sx={{ padding: 2 }}>
      <Typography variant="h5" gutterBottom>
        รายงานสินค้า {productPerformance.data?.shop?.name ?? ''}
      </Typography>

      {/* Filters */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
          <Grid item xs={12} lg={2}>
            <TextField
              size="small"
              select
              fullWidth
              label="บัญชี"
              value={shopId || ''}
              onChange={(e) => {
                setShopId(e.target.value)
              }}
            >
              {tiktokAccounts.map((account) => (
                <MenuItem key={account.shopId} value={account.shopId}>
                  {account.sellerName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="เลือกสินค้า"
              value={selectedProductName}
              onClick={handleOpenProductModal}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="วันที่เริ่มต้น"
              value={startDate}
              onChange={handleStartDateChange}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="วันที่สิ้นสุด"
              value={endDate}
              onChange={handleEndDateChange}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSearch}
              startIcon={<SearchOutlined />}
              fullWidth
            >
              ค้นหา
            </Button>
          </Grid>
        </Grid>
      </LocalizationProvider>

      {/* Product Selection Modal */}
      <ProductSelectionModal />

      {/* Antd Table */}
      <Spin spinning={productPerformance.isLoading}>
        {productPerformance.error ? (
          <Typography color="error" sx={{ my: 2 }}>
            เกิดข้อผิดพลาดในการโหลดข้อมูล
          </Typography>
        ) : tableData.length > 0 ? (
          <Table
            columns={columns}
            dataSource={tableData}
            bordered
            size="middle"
            pagination={false}
            scroll={{ x: 1500, y: 1000 }}
          />
        ) : (
          <Empty description="ไม่พบข้อมูลในช่วงเวลาที่เลือก" />
        )}
      </Spin>
    </Card>
  )
}

export default ProductReportCard
